import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'lucide-react';

export interface TimeFilter {
  limitDays?: number;
  startDate?: string;
  endDate?: string;
}

interface TimeFilterControlsProps {
  onFilterChange: (filter: TimeFilter) => void;
  defaultFilter?: TimeFilter;
}

const QUICK_FILTERS = [
  { label: 'All Time', value: null },
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 30 Days', value: 30 },
  { label: 'Last 90 Days', value: 90 },
  { label: 'Last 6 Months', value: 180 },
  { label: 'Last Year', value: 365 },
];

const TimeFilterControls: React.FC<TimeFilterControlsProps> = ({
  onFilterChange,
  defaultFilter,
}) => {
  const [selectedQuickFilter, setSelectedQuickFilter] = useState<number | null>(
    defaultFilter?.limitDays ?? null
  );
  const [startDate, setStartDate] = useState<string>(
    defaultFilter?.startDate || ''
  );
  const [endDate, setEndDate] = useState<string>(defaultFilter?.endDate || '');
  const [useCustomRange, setUseCustomRange] = useState<boolean>(
    !!(defaultFilter?.startDate || defaultFilter?.endDate)
  );
  const previousFilterRef = useRef<string>('');
  const onFilterChangeRef = useRef(onFilterChange);

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  useEffect(() => {
    let currentFilter: TimeFilter;
    
    if (!useCustomRange && selectedQuickFilter !== null) {
      currentFilter = { limitDays: selectedQuickFilter };
    } else if (useCustomRange && (startDate || endDate)) {
      currentFilter = {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };
    } else if (!useCustomRange && selectedQuickFilter === null) {
      currentFilter = {};
    } else {
      return;
    }

    const filterKey = JSON.stringify(currentFilter);
    if (filterKey !== previousFilterRef.current) {
      previousFilterRef.current = filterKey;
      onFilterChangeRef.current(currentFilter);
    }
  }, [selectedQuickFilter, startDate, endDate, useCustomRange]);

  const handleQuickFilterClick = (value: number | null) => {
    setSelectedQuickFilter(value);
    setUseCustomRange(false);
    setStartDate('');
    setEndDate('');
  };

  const handleCustomRangeToggle = () => {
    setUseCustomRange(!useCustomRange);
    if (!useCustomRange) {
      setSelectedQuickFilter(null);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <div className="flex items-center mb-4">
        <Calendar className="h-5 w-5 text-gray-600 mr-2" />
        <h3 className="text-sm font-medium text-gray-700">Time Filter</h3>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {QUICK_FILTERS.map((filter) => (
          <button
            key={filter.label}
            onClick={() => handleQuickFilterClick(filter.value ?? null)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              !useCustomRange &&
              selectedQuickFilter === (filter.value ?? null)
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={useCustomRange}
            onChange={handleCustomRangeToggle}
            className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Custom Date Range</span>
        </label>

        {useCustomRange && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Start Date"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="End Date"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeFilterControls;

